import { useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Autorenew, Eye, Trash2, Upload, X } from "@/components/ds/icons";
import { Tip } from "@/components/ds/Tip";

const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50MB — mismo tope que el catálogo de empresa

const ACCEPT_EXT = ".jpg,.jpeg,.png,.docx,.pdf,.xls,.xlsx";
const ACCEPT_MIME = [
  "image/jpeg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/pdf",
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
];

function fileExtensionAllowed(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return ACCEPT_EXT.split(",").includes(ext);
}

export interface DocumentoFilaAccionesProps {
  /** Nombre del tipo — sólo para los `aria-label`, que sin texto visible son
   * lo único que hace comprensibles a los botones de ícono. */
  label: string;
  tieneDocumento: boolean;
  /** Descarga / borrado / vigencia en curso para esta fila. */
  busy: boolean;
  uploading: boolean;
  /** 0-100. Sólo se usa cuando `uploading` es `true`. */
  uploadPercent: number;
  /** Se dispara con el archivo YA validado (mime/tamaño). */
  onPickFile: (file: File) => void;
  onCancelUpload: () => void;
  onDownload: () => void;
  onRequestDelete: () => void;
}

/**
 * Acciones de una fila de documentos. Lo comparten Documentación General del
 * trabajador y el panel de documentos de un recurso (EPP / vehículo /
 * maquinaria): las dos pantallas tienen el mismo modelo de slot —un archivo
 * por tipo, resubir reemplaza— así que también la misma botonera.
 *
 * Tres variantes excluyentes:
 *
 *   - sin archivo → `Subir documento`
 *   - subiendo    → porcentaje + `Cancelar`
 *   - con archivo → `visibility` ver · `autorenew` reemplazar · `delete` eliminar
 *
 * Las tres acciones de "con archivo" van SIN etiqueta para que la columna
 * respire, pero con el chrome del botón (`outline`: borde + fondo blanco), no
 * como glifo pelado. Es lo que hace el prototipo —`border:1px solid #e4e4e7;
 * background:#fff` en cada uno— y la diferencia importa: un ícono sin caja no
 * se lee como algo apretable, y estas tres son las acciones de la fila.
 *
 * DESVIACIÓN del prototipo: su tercera acción es `block` ("No aplica"). Ni los
 * documentos de trabajador ni los de recurso tienen esa marca —existe sólo
 * para el catálogo de empresa (`empresa_documentos_no_aplica`)— así que el
 * tercer botón es Eliminar, que es la acción que el backend sí soporta.
 *
 * `Subir` y `Reemplazar` abren el picker del sistema directamente, sin modal
 * intermedio, y comparten el mismo `onPickFile`.
 */
export function DocumentoFilaAcciones({
  label,
  tieneDocumento,
  busy,
  uploading,
  uploadPercent,
  onPickFile,
  onCancelUpload,
  onDownload,
  onRequestDelete,
}: DocumentoFilaAccionesProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function triggerPicker() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    // Reset inmediato para que volver a elegir el MISMO archivo dispare `onChange` de nuevo.
    e.target.value = "";
    if (!selected) return;

    if (selected.size > MAX_SIZE_BYTES) {
      toast.error("El archivo no puede superar los 50 MB.");
      return;
    }

    const mimeOk = ACCEPT_MIME.includes(selected.type);
    const extOk = fileExtensionAllowed(selected.name);
    if (!mimeOk && !extOk) {
      toast.error("Solo se aceptan archivos JPG, PNG, DOCX, PDF, XLS o XLSX.");
      return;
    }

    onPickFile(selected);
  }

  if (uploading) {
    return (
      <div className="flex shrink-0 items-center justify-end gap-2">
        <span className="text-muted-foreground fc-num text-xs">{uploadPercent}%</span>
        <Button variant="outline" size="xs" onClick={onCancelUpload}>
          <X className="h-3.5 w-3.5" />
          Cancelar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex shrink-0 items-center justify-end gap-1">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_EXT}
        className="hidden"
        onChange={handleFileChange}
      />

      {tieneDocumento ? (
        <>
          <Tip texto="Ver documento">
            <Button
              variant="outline"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              disabled={busy}
              onClick={onDownload}
              aria-label={`Ver ${label}`}
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip texto="Reemplazar documento">
            <Button
              variant="outline"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              disabled={busy}
              onClick={triggerPicker}
              aria-label={`Reemplazar ${label}`}
            >
              <Autorenew className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip texto="Eliminar documento">
            <Button
              variant="outline"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={busy}
              onClick={onRequestDelete}
              aria-label={`Eliminar ${label}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </>
      ) : (
        <Button variant="outline" size="xs" disabled={busy} onClick={triggerPicker}>
          <Upload className="h-3.5 w-3.5" />
          Subir documento
        </Button>
      )}
    </div>
  );
}
